import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { qk } from './keys';
import type { Health, StudentProfile, TutorCard, UserProfile } from '../types/api';

export function useMe(enabled = true) {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => api.get<UserProfile>('/me'),
    enabled,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      fullName?: string;
      avatarUrl?: string;
      phone?: string;
      timezone?: string;
    }) => api.patch<UserProfile>('/me', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.me }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<{ success: true }>('/me/password', input),
  });
}

// Role-specific profiles — only reachable as that role, which in this app
// means while impersonating.

export function useMyTutorProfile(enabled: boolean) {
  return useQuery({
    queryKey: qk.myTutorProfile,
    queryFn: () => api.get<TutorCard>('/tutors/me'),
    enabled,
  });
}

export function useUpdateMyTutorProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      headline?: string;
      bio?: string;
      subjects?: string[];
      hourlyRateCents?: number;
      currency?: string;
      yearsExperience?: number;
    }) => api.patch<TutorCard>('/tutors/me', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.myTutorProfile });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useMyStudentProfile(enabled: boolean) {
  return useQuery({
    queryKey: qk.myStudentProfile,
    queryFn: () => api.get<StudentProfile>('/students/me'),
    enabled,
  });
}

export function useUpdateMyStudentProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      gradeLevel?: string;
      schoolName?: string;
      guardianEmail?: string;
      guardianPhone?: string;
    }) => api.patch<StudentProfile>('/students/me', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.myStudentProfile });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

/** Unauthenticated probe — drives the API status pill in the sidebar. */
export function useHealth() {
  return useQuery({
    queryKey: qk.health,
    queryFn: () => api.get<Health>('/health'),
    refetchInterval: 60_000,
    retry: false,
  });
}
